/**
 * Данные кабинета хозяйства — один хук на экран. Каждый блок получает свой
 * ответ сервера; на клиенте только раскладка (`model.ts`) и правила
 * приоритета (`priority.ts`).
 *
 * Откуда что:
 * * `/actors/{id}` — имя, массив, направления реестра;
 * * `/season/cycles?actor_id` — циклы хозяйства (идущие, прошлые) с ходом;
 * * `/season/picture?cycle&actor_id&summary` — работы идущих циклов;
 * * `/season/farm-breakdown?cycle&actor_id` — проблемы идущих циклов;
 * * `/farms/{id}/land`, `/farms/{id}/quarantine` — земля и карантин (6al.34);
 * * `/ai/metrics?actor_id&cycle` — ключевые показатели слоя метрик: те же
 *   определения, что на экранах района.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, request, type SituationList } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { useServerToday } from '../../../analysis/useSliceDate';
import { localIsoDate } from '../../../lib/localDate';
import { useAnalysisContext } from '../../../analysis/context';
import { loadFarmBreakdown } from '../../astra/problems';
import {
  FARM_METRIC_IDS,
  farmFacts,
  metricMap,
  primaryCycle,
  type FarmBreakdown,
  type FarmLand,
  type FarmQuarantine,
  type MetricValue,
} from './model';
import { arrangeFarm } from './priority';

export function useFarmCabinet(farmId: number) {
  const ctx = useAnalysisContext();
  const [search] = useSearchParams();
  const { params, key } = ctx;

  const actor = useRequest(() => api.actor(farmId, params), [farmId, key]);
  const cycles = useRequest(() => api.seasonCycles({ ...params, actor_id: farmId }), [farmId, key]);
  const land = useRequest(() => request<FarmLand>(`/farms/${farmId}/land`, params), [farmId, key]);
  const quarantine = useRequest(() => request<FarmQuarantine>(`/farms/${farmId}/quarantine`, params), [farmId, key]);

  // Циклы ХОЗЯЙСТВА — те, где у него есть обязательства: районный цикл без
  // обязательств хозяйства приходит «запланированным» с пустым окном, и
  // показывать его как «подготовку» хозяйства значило бы выдумать участие.
  const items = useMemo(() => (cycles.data?.items ?? []).filter((c) => c.obligations.total > 0), [cycles.data]);
  const active = useMemo(() => items.filter((c) => c.status === 'active'), [items]);
  const activeKey = active.map((c) => c.cycle).join(',');
  const primary = primaryCycle(items, search.get('cycle'));
  // «Сегодня» и срез — числа СЕРВЕРА (`horizon`), не часы браузера: иначе кабинет
  // говорил бы «срез 11 окт» рядом с «на срезе 12 окт». Часы вкладки — лишь
  // страховка на миг, пока сервер не ответил (экран в это время грузится).
  const serverNow = useServerToday();
  const today = serverNow ?? (ctx.asOf ? null : cycles.data?.horizon ?? null) ?? localIsoDate();
  const asOf = ctx.asOf ?? today;

  /** Работы и проблемы КАЖДОГО идущего цикла: «Сейчас» говорит обо всех. */
  const current = useRequest(
    () =>
      Promise.all(
        active.map(async (c) => {
          const [picture, breakdown] = await Promise.all([
            api.seasonPicture({ ...params, cycle: c.cycle, actor_id: farmId, summary: true, weeks: 0 }),
            loadFarmBreakdown(params, c.cycle, farmId).catch(() => null) as Promise<FarmBreakdown | null>,
          ]);
          return { cycle: c, picture, breakdown };
        }),
      ),
    [farmId, key, activeKey],
  );

  const metrics = useRequest(
    () =>
      request<{ metrics: MetricValue[] }>('/ai/metrics', {
        ...params,
        ids: FARM_METRIC_IDS.join(','),
        actor_id: farmId,
        ...(primary ? { cycle: primary.cycle } : {}),
      }),
    [farmId, key, primary?.cycle],
  );
  const metricById = useMemo(() => metricMap(metrics.data?.metrics), [metrics.data]);

  const situations = useRequest(
    () =>
      primary
        ? api.situations({ ...params, season: primary.season_label, actor_id: farmId, lens: 'all', limit: 5 }).catch(() => null)
        : Promise.resolve(null),
    [farmId, key, primary?.season_label],
  );

  // «Сейчас» относится к набору идущих циклов: пока ответ не про текущий набор
  // (первый ответ пришёл на пустой список до циклов), это загрузка, а не
  // «ничего не идёт» (6al.49). Ошибка — не загрузка.
  const currentLoading =
    current.state !== 'error' && (current.data === null || current.data.map((r) => r.cycle.cycle).join(',') !== activeKey);
  const factsReady = cycles.state !== 'loading' && !currentLoading && quarantine.state !== 'loading' && metrics.state !== 'loading';
  const facts = useMemo(
    () =>
      farmFacts({
        asOf,
        today,
        cycles: items,
        breakdowns: (current.data ?? []).map((c) => c.breakdown),
        quarantine: quarantine.data,
        metrics: metricById,
      }),
    [asOf, today, items, current.data, quarantine.data, metricById],
  );
  const arrangement = useMemo(() => arrangeFarm(facts), [facts]);

  return {
    ctx,
    asOf,
    today,
    actor,
    cycles,
    items,
    active,
    primary,
    current,
    currentLoading,
    land,
    quarantine,
    metrics,
    metricById,
    situations: situations.data as SituationList | null,
    facts,
    factsReady,
    arrangement,
  };
}

export type FarmCabinetData = ReturnType<typeof useFarmCabinet>;
